import React, { ReactElement, Suspense, useEffect } from 'react';
import { QueryClientProvider, useQueryErrorResetBoundary } from 'react-query';
import { Router } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { Loading } from 'components/Loading';
import MainFrame from 'components/MainFrame';
import { ErrorFallback } from 'components/ErrorFallback';

import { ModalProvider } from 'contexts/ModalProvider';
import { historian } from 'utils/historian';
import { WizardProvider } from 'contexts/WizardProvider';
import history from 'configs/history';
import queryClient from 'configs/reactQuery';
import { applicationService } from 'services/application';
import { viewPage } from 'utils/viewPage';
import { LayoutProvider } from 'contexts/LayoutProvider';
import { RaffleCartProvider } from 'contexts/RaffleCartProvider/RaffleCartProvider';
import 'styles/styles.scss';
import { configManager } from 'utils/configuration';
import { RouterMapper } from 'utils/routerMapper';
import { UserFlagProvider } from 'contexts/UserFlagProvider';
import { sendEvent } from 'utils/send';
import { isMobileAgent } from 'utils/isMobileAgent';
import { isPublicUrl } from 'utils/isPublicUrl';
import { resolveFromToken } from 'utils/resolveFromToken';

function App(): ReactElement {
  const { reset } = useQueryErrorResetBoundary();

  configManager.getInstance().load();

  useEffect(() => {
    window.hj?.('identify', resolveFromToken('userId'));

    viewPage(window.location.pathname);
    const historyListen = history.listen((location) => {
      historian({ action: history.action, location });
      viewPage(location.pathname);
    });
    return () => {
      sendEvent.close();
      // clean history listen
      historyListen();
    };
  }, []);

  useEffect(() => {
    import('react-hotjar').then(({ hotjar }) => {
      if (hotjar.initialized()) {
        hotjar.identify(resolveFromToken('userId'), {
          userId: resolveFromToken('userId'),
        });
      }
    });
  }, [applicationService]);

  const showContent = isMobileAgent() || isPublicUrl();

  if (!showContent) {
    return <></>;
  }

  return (
    <ErrorBoundary onReset={reset} FallbackComponent={ErrorFallback}>
      <MainFrame>
        <QueryClientProvider client={queryClient}>
          <ModalProvider>
            <WizardProvider>
              <UserFlagProvider>
                <LayoutProvider>
                  <RaffleCartProvider>
                    <Router history={history}>
                      <Suspense fallback={<Loading />}>
                        <RouterMapper />
                      </Suspense>
                    </Router>
                  </RaffleCartProvider>
                </LayoutProvider>
              </UserFlagProvider>
            </WizardProvider>
          </ModalProvider>
        </QueryClientProvider>
      </MainFrame>
    </ErrorBoundary>
  );
}

export default App;
